import _get from 'lodash/get';
import _set from 'lodash/set';
import type { Paths } from 'type-fest';

import type { ApplicationFormData } from '../../../api';
import {
  type QuestionsStepsValues,
  Steps,
  type StepsValues,
} from '../../constants';
import type { State } from '../../types';

type ApplicationFormDataKeys = Paths<ApplicationFormData>;
type FlagsKeys = Extract<ApplicationFormDataKeys, `${string}isNo${string}`>;
type DetailsKeys = Extract<
  ApplicationFormDataKeys,
  `${string}Details` | `${string}Entities`
>;

const mapSteps: Record<
  QuestionsStepsValues,
  {
    flag: FlagsKeys;
    details?: DetailsKeys;
    getNextStep: (answer: boolean) => StepsValues;
  }
> = {
  [Steps.PREVIOUS_NAMES_QUESTION]: {
    flag: 'oldFioData.isNoChangeFio',
    details: 'oldFioData.oldFioDetails',
    getNextStep: (answer) =>
      answer
        ? Steps.PREVIOUS_NAMES_FORM
        : Steps.CIVIL_SERVICE_EXPERIENCE_QUESTION,
  },
  [Steps.CIVIL_SERVICE_EXPERIENCE_QUESTION]: {
    flag: 'governmentWorkData.isNoGovernmentWork',
    getNextStep: (answer) =>
      answer
        ? Steps.CIVIL_SERVICE_EXPERIENCE_UNAVAILABLE
        : Steps.RGS_EMPLOYEE_RELATIVES_QUESTION,
  },
  [Steps.RGS_EMPLOYEE_RELATIVES_QUESTION]: {
    flag: 'relativesData.isNoRelatives',
    details: 'relativesData.relativesDetails',
    getNextStep: (answer) =>
      answer
        ? Steps.RGS_EMPLOYEE_RELATIVES_FORM
        : Steps.PROFESSIONAL_EDUCATION_QUESTION,
  },
  [Steps.PROFESSIONAL_EDUCATION_QUESTION]: {
    flag: 'educationData.isNoEducation',
    details: 'educationData.educationDetails',
    getNextStep: (answer) =>
      answer
        ? Steps.PROFESSIONAL_EDUCATION_FORM
        : Steps.INSURANCE_AGENT_EXPERIENCE_QUESTION,
  },
  [Steps.INSURANCE_AGENT_EXPERIENCE_QUESTION]: {
    flag: 'insuranceAgentWorkData.isNoInsuranceAgentWork',
    details: 'insuranceAgentWorkData.insuranceAgentWorkDetails',
    getNextStep: (answer) =>
      answer
        ? Steps.INSURANCE_AGENT_EXPERIENCE_LIST_FORM
        : Steps.OTHER_WORK_EXPERIENCE_QUESTION,
  },
  [Steps.OTHER_WORK_EXPERIENCE_QUESTION]: {
    flag: 'workData.isNoWork',
    details: 'workData.workDetails',
    getNextStep: (answer) =>
      answer
        ? Steps.OTHER_WORK_EXPERIENCE_FORM
        : Steps.LEGAL_ENTITY_FOUNDER_STATUS_QUESTION,
  },
  [Steps.LEGAL_ENTITY_FOUNDER_STATUS_QUESTION]: {
    flag: 'legalEntitiesData.isNoLegalEntities',
    details: 'legalEntitiesData.legalEntities',
    getNextStep: (answer) =>
      answer
        ? Steps.LEGAL_ENTITY_FOUNDER_STATUS_FORM
        : Steps.SELF_EMPLOYMENT_STATUS_QUESTION,
  },
  [Steps.SELF_EMPLOYMENT_STATUS_QUESTION]: {
    flag: 'selfEmployedWorkData.isNoSelfEmployedWork',
    details: 'selfEmployedWorkData.selfEmployedWorkDetails',
    getNextStep: (answer) =>
      answer ? Steps.SELF_EMPLOYMENT_STATUS_FORM : Steps.QUESTIONNAIRE_RESULTS,
  },
};

export const getAnswerQuestionUpdate = (
  state: State,
  questionStep: QuestionsStepsValues,
  answer: boolean
): {
  nextStep: StepsValues;
  formUpdate?: ApplicationFormData;
} => {
  const stepObj = mapSteps[questionStep];

  const nextStep = stepObj.getNextStep(answer);
  let formUpdate: undefined | ApplicationFormData;

  const prevFlagValue = _get(state, stepObj.flag);
  const nextFlagValue = !answer; // invert because all fields are "isNo"

  const answerWasChanged = nextFlagValue !== prevFlagValue;
  const needToSendYesAnswer =
    answer &&
    answerWasChanged &&
    questionStep === Steps.CIVIL_SERVICE_EXPERIENCE_QUESTION;
  const needToSendNoAnswer = !answer && answerWasChanged;

  if (needToSendYesAnswer || needToSendNoAnswer) {
    formUpdate = {};

    _set(formUpdate, stepObj.flag, nextFlagValue);

    if (stepObj.details) {
      _set(formUpdate, stepObj.details, undefined);
    }
  }

  return {
    nextStep,
    formUpdate,
  };
};
